import { Component, computed, inject, output, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { NotifyService } from '../../services/notify.service';

/**
 * Modal บังคับเปลี่ยน password — แสดงเมื่อ MeResponse.changePasswordFirst = true
 * - Overlay เต็มหน้าจอ ปิดด้วย click outside / ESC ไม่ได้ (บังคับให้เปลี่ยน)
 * - 2 input: password + confirmPassword
 * - submit → PUT /api/auth/change-password-first
 * - สำเร็จ → emit changed (parent reload /api/auth/me)
 */
@Component({
  selector: 'app-force-change-password-modal',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './force-change-password-modal.html',
})
export class ForceChangePasswordModal {
  private readonly http = inject(HttpClient);
  private readonly notify = inject(NotifyService);

  /** emit เมื่อเปลี่ยน password สำเร็จ — parent ต้อง reload /api/auth/me */
  readonly changed = output<void>();

  /** รหัสผ่านใหม่ */
  readonly password = signal('');
  /** ยืนยันรหัสผ่านใหม่ */
  readonly confirmPassword = signal('');
  /** toggle แสดง/ซ่อนรหัสผ่าน */
  readonly showPassword = signal(false);
  readonly showConfirm = signal(false);
  /** สถานะกำลังส่ง request */
  readonly isSubmitting = signal(false);
  /** error จาก server */
  readonly serverError = signal('');

  /** error password — แสดง live ทันทีที่พิมพ์ (กฎเดียวกับ register/profile: ≥6 ตัวอักษร) */
  readonly passwordError = computed(() => {
    const v = this.password();
    if (!v) return ''; // ยังไม่กรอก ไม่ต้องแจ้ง
    if (v.length < 6) return 'รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร';
    return '';
  });

  /** error confirm — แสดง live เมื่อ user เริ่มกรอก confirm */
  readonly confirmError = computed(() => {
    const c = this.confirmPassword();
    if (!c) return '';
    if (this.password() !== c) return 'รหัสผ่านไม่ตรงกัน';
    return '';
  });

  /** form valid */
  readonly isValid = computed(() =>
    this.password().length >= 6
    && this.password() === this.confirmPassword()
  );

  /** submit เปลี่ยน password */
  submit(): void {
    this.serverError.set('');
    if (!this.isValid()) return;

    this.isSubmitting.set(true);
    this.http.put<{ success: boolean; message?: string }>(
      '/api/auth/change-password-first',
      { password: this.password(), confirm_password: this.confirmPassword() },
    ).subscribe({
      next: res => {
        this.isSubmitting.set(false);
        if (res.success) {
          this.notify.success('เปลี่ยนรหัสผ่านสำเร็จ', 'เริ่มใช้งานระบบได้เลย');
          this.changed.emit();
        } else {
          this.serverError.set(res.message ?? 'เปลี่ยนรหัสผ่านไม่สำเร็จ');
        }
      },
      error: (err: { error?: { message?: string } }) => {
        this.isSubmitting.set(false);
        this.serverError.set(err?.error?.message ?? 'เกิดข้อผิดพลาด');
      },
    });
  }
}
